import device from "current-device";
import { throttle } from "throttle-debounce";
import { m as matchesAbGroup } from "./cookie-helpers-PDAUk5j7.js";
const msntItemAbTestTargetCls = "msnt-item-abtest-candidate";
const msntItemAbTestTargetSelector = `.${msntItemAbTestTargetCls}`;
const defaultFroomleSettings = {
  enableLogging: false,
  logNS: "[MSNT FROOMLE]",
  batchPeriodEvents: 1e3,
  batchPeriodWidgets: 100,
  initialRequestTimeout: 500,
  refreshTokenThreshold: 60 * 30 * 1e3,
  loadFirstWidgetASAP: true,
  froomleUrl: "",
  proxyUrl: "",
  idPrefix: "",
  froomleItemCls: "msnt-froomle-item",
  froomleItemSelector: ".msnt-froomle-item",
  froomleMissingRecommendationCls: "msnt-froomle-no-item",
  widgetNoRecommendationsCls: "msnt-froomle-no-recommendations",
  msntItemSelector: "",
  msntSkeletonItemSelector: "",
  msntSkeletonItemCls: "",
  msntItemAbTestTargetCls,
  msntItemAbTestTargetSelector,
  msntRecommendationContainerSelector: "",
  nonScopedBlocksSelectors: {},
  nonScopedBlocksItemSelectors: {},
  scopedBlocksSelectors: {},
  scopedBlocksItemSelectors: {},
  ajaxRetries: 3,
  oauthRetries: -1,
  recommendationsThreshold: 0,
  recommendationsMargin: "100%",
  impressionThreshold: 0.25,
  impressionMargin: "",
  useProxyForAllRequests: false,
  sendPageViews: true,
  showRecommendations: true,
  sendImpressions: true,
  sendRecommendationClicks: true,
  sendNonFroomleClicksToFroomle: false,
  sendNonFroomleImpressionsToFroomle: false,
  excludeContentOnlyUntilWidget: true,
  lists: {},
  hooks: {},
  updateItemToFroomleRecommendation: () => {
  },
  updateItemToFroomleRecommendationScoped: () => {
  },
  minImageSize: 200,
  maxImageSize: 1200,
  imageSizesStep: 100,
  mediaTypeToExtension: {
    "image/webp": "webp",
    "image/jpeg": "jpg"
  },
  race_types: []
};
const froomleSettings = defaultFroomleSettings;
const deviceIdKey = "msntDeviceId";
const froomleOAuthTokenKey = "msntFroomleOAuthToken";
let currentAttempt = 1;
const retrieveOAuthToken = () => {
  let token = null;
  try {
    const data = localStorage.getItem(froomleOAuthTokenKey);
    if (data) {
      token = JSON.parse(data);
    }
  } catch (err) {
    logDebug(`Unable to retrieve "${froomleOAuthTokenKey}"`, [err], "warn");
  }
  return token;
};
let cachedOAuthToken = retrieveOAuthToken();
const getOAuthToken = async () => {
  if (currentAttempt > froomleSettings.oauthRetries) {
    logDebug(
      `Unable to retrieve OAuth token after ${froomleSettings.oauthRetries} attempts. Froomle won't be initialized`,
      void 0,
      "warn"
    );
    currentAttempt = 1;
    return null;
  }
  const token = cachedOAuthToken;
  if (!token || !isOAuthTokenStillValid(token)) {
    await updateOAuthToken();
    return await getOAuthToken();
  }
  return token;
};
const isOAuthTokenStillValid = (token) => {
  const diff = token.expires_at - (/* @__PURE__ */ new Date()).getTime();
  const isValid = diff > froomleSettings.refreshTokenThreshold;
  if (!isValid) {
    logDebug("OAuth token has expired, loading new one");
  }
  return isValid;
};
const storeOAuthToken = (token) => {
  try {
    localStorage.setItem(froomleOAuthTokenKey, JSON.stringify(token));
    cachedOAuthToken = token;
  } catch (err) {
    logDebug(`Unable to save "${froomleOAuthTokenKey}"`, [err], "warn");
  }
};
const updateOAuthToken = async () => {
  currentAttempt++;
  return fetch(`${froomleSettings.proxyUrl}/froomle/auth/`, {
    method: "POST"
  }).then((data) => data.json()).then((response) => {
    let tokenData;
    try {
      tokenData = JSON.parse(response.data);
      if (!tokenData.expires_in || !tokenData.access_token) {
        throw new Error("Token is invalid");
      }
    } catch (err) {
      logDebug("Failed to retrieve access token information", [err], "warn");
      return null;
    }
    currentAttempt = 1;
    const newToken = {
      ...tokenData,
      expires_at: (/* @__PURE__ */ new Date()).getTime() + tokenData.expires_in * 1e3
    };
    storeOAuthToken(newToken);
    return newToken;
  }).catch((err) => {
    logDebug(
      `Unable to get OAuth token. Attempt ${currentAttempt - 1} of ${froomleSettings.oauthRetries}`,
      [err],
      "warn"
    );
    return null;
  });
};
const logDebug = (msg, args = [], level = "log") => {
  if (!froomleSettings.enableLogging) return;
  console[level](froomleSettings.logNS, msg, args.length ? "\n" : "", ...args);
};
const getUserId = /* @__PURE__ */ (() => {
  let cachedValue = null;
  return () => {
    var _a;
    if (cachedValue) return cachedValue;
    const userId = (_a = froomleSettings.user) == null ? void 0 : _a.userId;
    cachedValue = userId ? `${userId}` : null;
    return cachedValue;
  };
})();
const getDeviceId = /* @__PURE__ */ (() => {
  let cachedValue;
  return () => {
    if (cachedValue) return cachedValue;
    let value = null;
    try {
      value = localStorage.getItem(deviceIdKey);
    } catch (err) {
      logDebug(
        `Unable to retrieve previously stored "${deviceIdKey}"`,
        [err],
        "warn"
      );
    }
    if (!value) {
      const userId = getUserId();
      if (userId) {
        value = userId;
      }
    }
    if (!value) {
      const randomInt = Math.trunc(Math.random() * 1e10);
      value = `${crypto.randomUUID()}--${randomInt}`;
      try {
        localStorage.setItem(deviceIdKey, value);
      } catch (err) {
        logDebug(`Unable to save unique "${deviceIdKey}"`, [err], "warn");
      }
    }
    cachedValue = value;
    return value;
  };
})();
const getChannel = /* @__PURE__ */ (() => {
  let cachedValue;
  return () => {
    if (cachedValue) return cachedValue;
    const type = device.desktop() ? "desktop" : "mobile";
    cachedValue = `www-${type}`;
    return cachedValue;
  };
})();
const getEdition = /* @__PURE__ */ (() => {
  let cachedValue;
  return () => {
    var _a;
    if (cachedValue) return cachedValue;
    const pageProps = (_a = window.dataLayer) == null ? void 0 : _a[0];
    if (!pageProps) return cachedValue;
    cachedValue = pageProps.currentEdition;
    return cachedValue;
  };
})();
const getAbTestVersion = /* @__PURE__ */ (() => {
  let cachedValue = "";
  return () => {
    var _a, _b, _c, _d;
    if (cachedValue) return cachedValue;
    try {
      const val = (_d = (_a = window.msntConfigVars) == null ? void 0 : _a.abtestGroups) == null ? void 0 : _d[(_c = (_b = window.msntConfigVars) == null ? void 0 : _b.froomle) == null ? void 0 : _c.abtest.name];
      return `froomle_${val}`;
    } catch {
      logDebug(`Unable to retrieve user abtest group`, void 0, "warn");
    }
    return cachedValue;
  };
})();
const isFroomleAbGroup = /* @__PURE__ */ (() => {
  let cachedValue;
  return (settings = froomleSettings) => {
    if (cachedValue !== void 0) return cachedValue;
    const abTestCfg = settings == null ? void 0 : settings.abtest;
    if (!abTestCfg) return true;
    cachedValue = matchesAbGroup(abTestCfg.name, {
      min: abTestCfg.min,
      max: abTestCfg.max,
      prefix: abTestCfg.prefix
    });
    return cachedValue;
  };
})();
const mergeAndNormalizeSettings = (settings) => {
  Object.assign(froomleSettings, settings, {
    nonScopedBlocksSelectors: mergeScopedBlockSelectors(
      froomleSettings.nonScopedBlocksSelectors,
      settings == null ? void 0 : settings.nonScopedBlocksSelectors
    ),
    nonScopedBlocksItemSelectors: mergeScopedBlockSelectors(
      froomleSettings.nonScopedBlocksItemSelectors,
      settings == null ? void 0 : settings.nonScopedBlocksItemSelectors
    ),
    scopedBlocksSelectors: mergeScopedBlockSelectors(
      froomleSettings.scopedBlocksSelectors,
      settings == null ? void 0 : settings.scopedBlocksSelectors
    ),
    scopedBlocksItemSelectors: mergeScopedBlockSelectors(
      froomleSettings.scopedBlocksItemSelectors,
      settings == null ? void 0 : settings.scopedBlocksItemSelectors
    ),
    lists: Object.assign(froomleSettings.lists, settings == null ? void 0 : settings.lists)
  });
  if (document.cookie.includes("msntFroomleLogs=1")) {
    froomleSettings.enableLogging = true;
  }
  if (froomleSettings.oauthRetries === -1) {
    froomleSettings.oauthRetries = froomleSettings.ajaxRetries;
  }
  if (settings == null ? void 0 : settings.useProxyForAllRequests) {
    logDebug(`Requests proxy is enabled.`);
    froomleSettings.froomleUrl = froomleSettings.proxyUrl;
  } else {
    logDebug(
      `Requests proxy disabled. Froomle API will be directly used for all requests.`
    );
  }
  if (!isFroomleAbGroup()) {
    froomleSettings.showRecommendations = false;
    froomleSettings.sendImpressions = false;
    if (froomleSettings.sendNonFroomleImpressionsToFroomle) {
      froomleSettings.sendImpressions = true;
    }
    if (froomleSettings.sendNonFroomleClicksToFroomle) {
      froomleSettings.sendRecommendationClicks = true;
    }
  }
  logDebug(`Settings after merge`, [froomleSettings]);
};
const mergeScopedBlockSelectors = (initial, incoming) => {
  const result = structuredClone(initial);
  if (!incoming) return result;
  for (const key in incoming) {
    if (!result[key]) {
      result[key] = incoming[key];
    } else {
      Object.assign(result[key], incoming[key]);
    }
  }
  return result;
};
const isSettingsValid = (settings) => {
  const criticalErrors = [];
  if (window.location.protocol !== "https:") {
    criticalErrors.push('Non secure context, switch to "https" protocol.');
  }
  if (typeof settings.proxyUrl !== "string") {
    criticalErrors.push(`No "proxyUrl" provided.`);
  }
  if (!settings.useProxyForAllRequests && !settings.froomleUrl) {
    criticalErrors.push(
      `We don't use proxy for requests to Froomle, but no "froomleUrl" provided `
    );
  }
  if (criticalErrors.length) {
    logDebug(
      `Critical settings issues detected. Froomle won't be initialized:`,
      [criticalErrors].map((item) => `
- ${JSON.stringify(item)}`),
      "error"
    );
  }
  return !criticalErrors.length;
};
const getFroomlePageType = /* @__PURE__ */ (() => {
  let cachedValue;
  return () => {
    var _a;
    if (cachedValue !== void 0) return cachedValue;
    cachedValue = null;
    const pageProps = (_a = window.dataLayer) == null ? void 0 : _a[0];
    if (!pageProps) return cachedValue;
    if (pageProps.entityType === "article" && pageProps.pageType === "detail") {
      cachedValue = "article_detail";
    } else if ([
      "home",
      "home-series",
      "main"
      // 'home-category' - Skip for now. We would need to have list of category series on run on this page. Don't forget to enable in GTM
    ].includes(pageProps.pageType)) {
      cachedValue = "home";
    }
    if (!cachedValue) {
      logDebug(
        `Page views tracking not setup for this page. 
Events won't be sent!`,
        void 0,
        "warn"
      );
      return;
    }
    return cachedValue;
  };
})();
const sendFroomleEvent = (() => {
  let collectedEvents = [];
  const batchSendFroomleEventsThrottled = throttle(
    froomleSettings.batchPeriodEvents,
    () => {
      batchSendFroomleEvents(collectedEvents);
      collectedEvents = [];
    },
    {
      noLeading: true
    }
  );
  return (eventType, pageType, params, flush = false, processEventBeforeSend) => {
    var _a, _b, _c;
    const eventParams = {
      event_type: eventType,
      page_type: pageType,
      device_id: getDeviceId(),
      channel: getChannel(),
      ...params
    };
    const abtestVersion = getAbTestVersion();
    if (abtestVersion) {
      eventParams.version = abtestVersion;
    }
    if (eventType === "detail_pageview" || eventType === "page_visit") {
      eventParams.data = eventParams.data ?? {};
      Object.assign(eventParams.data, {
        isEditor: (_a = froomleSettings.user) == null ? void 0 : _a.isEditor
      });
    }
    (_c = (_b = froomleSettings.hooks) == null ? void 0 : _b.setAdditionalEventData) == null ? void 0 : _c.call(
      _b,
      froomleSettings,
      eventType,
      eventParams
    );
    const userId = getUserId();
    if (userId) {
      eventParams.user_id = userId;
    }
    processEventBeforeSend == null ? void 0 : processEventBeforeSend(eventParams);
    collectedEvents.push(eventParams);
    if (flush) {
      batchSendFroomleEvents(collectedEvents);
      collectedEvents = [];
    } else {
      batchSendFroomleEventsThrottled();
    }
  };
})();
const batchSendFroomleEvents = (events) => {
  let attempt = 1;
  let retryRequest;
  (retryRequest = async () => {
    try {
      const response = await fetch(`${froomleSettings.froomleUrl}/events`, {
        ...await getCommonAjaxParams(),
        keepalive: true,
        body: JSON.stringify({
          events
        })
      });
      if (!response.ok) {
        if (response.status === 400) {
          const data = await response.json();
          logDebug(
            `Unable send events due to Bad request.`,
            [data.detail],
            "error"
          );
          return;
        }
        throw new Error(response.statusText);
      }
      logDebug(`Events successfully logged`, [events, response]);
    } catch (err) {
      logDebug(
        `Failed to send events. Attempt ${attempt} of ${froomleSettings.ajaxRetries}`,
        [events, err],
        "warn"
      );
      if (attempt < froomleSettings.ajaxRetries) {
        attempt++;
        retryRequest();
      } else {
        logDebug(
          `Unable to send events to Froomle after ${froomleSettings.ajaxRetries} attempts.`,
          [events, err],
          "error"
        );
      }
    }
  })();
};
const getCommonAjaxParams = /* @__PURE__ */ (() => {
  let cachedValue;
  return async () => {
    if (cachedValue) return cachedValue;
    const headers = {
      "Content-Type": "application/json"
    };
    if (!froomleSettings.useProxyForAllRequests) {
      const token = await getOAuthToken();
      headers.Authorization = `Bearer ${token.access_token}`;
    }
    const edition = getEdition();
    if (edition) {
      headers.Location = edition;
    }
    cachedValue = {
      method: "POST",
      headers
    };
    return cachedValue;
  };
})();
const widgetToWidgetParamsMap = /* @__PURE__ */ new Map();
const initializedWidgetsSet = /* @__PURE__ */ new Set();
const initializedWidgetElementsSet = /* @__PURE__ */ new WeakSet();
const itemsTrackingParamsMap = /* @__PURE__ */ new Map();
const listNameToRecommendationsMap = /* @__PURE__ */ new Map();
const duplicatedWidgetsToProcess = /* @__PURE__ */ new Set();
const widgetsAwaitingConsent = /* @__PURE__ */ new Set();
const widgetsWithClickTrackingInitialized = /* @__PURE__ */ new Set();
const ongoingRecommendationLists = /* @__PURE__ */ new Map();
const ongoingRecommendationListsExcludes = /* @__PURE__ */ new Map();
const widgetsWithAnalyticsInitialized = /* @__PURE__ */ new Set();
const startFroomleAnalyticsForWidget = (widget) => {
  if (!froomleSettings.sendRecommendationClicks) return;
  const widgetName = getWidgetName(widget);
  if (!widgetName) return;
  const alreadyInitialized = widgetsWithAnalyticsInitialized.has(widget);
  if (alreadyInitialized) return;
  widgetsWithAnalyticsInitialized.add(widget);
  logDebug("GA clicks tracking enabled for widget", [widget]);
  widget.addEventListener("click", (event) => {
    var _a;
    const item = event.target.closest(
      // we want to track all items in widgets like "latest-news" on homepage, which won't have abtest class
      // froomleSettings.msntItemAbTestTargetSelector,
      froomleSettings.msntItemSelector
    );
    if (!item) return;
    const froomleItemId = getPlainIdForItem(item);
    if (!froomleItemId) {
      logDebug("Unable to retrieve froomleItemId for item ", [item], "error");
      return;
    }
    (_a = window.dataLayer) == null ? void 0 : _a.push({
      event: "froomleClickTracking",
      value: getItemType(item),
      froomleItemId,
      froomlePlacement: widgetName
    });
  });
};
const getItemType = (item) => {
  if (item.classList.contains(froomleSettings.froomleMissingRecommendationCls))
    return "empty";
  if (item.classList.contains(froomleSettings.froomleItemCls)) return "froomle";
  if (isFroomleAbGroup()) {
    if (item.dataset.froomleCustomStatus) {
      return item.dataset.froomleCustomStatus;
    } else if (item.classList.contains(froomleSettings.msntItemAbTestTargetCls)) {
      return "pending";
    } else {
      return "froomle-ignored";
    }
  }
  return "original";
};
const trackImpressionsForItemsInBlock = (pageType, context) => {
  if (!froomleSettings.sendImpressions) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        sendFroomleEvent(
          "impression",
          pageType,
          extractParamsFromRecommendationItem$1(entry.target)
        );
        observer.unobserve(entry.target);
      }
    },
    {
      rootMargin: froomleSettings.impressionMargin,
      threshold: froomleSettings.impressionThreshold
    }
  );
  context.querySelectorAll(froomleSettings.msntItemSelector).forEach((el) => observer.observe(el));
};
const extractParamsFromRecommendationItem$1 = (item) => {
  const data = extractFroomleParamsFromItem(item);
  return data;
};
const startRecommendationsClickTrackingInWidget = (context) => {
  if (!froomleSettings.sendRecommendationClicks) return;
  const pageType = getFroomlePageType();
  if (!pageType) return;
  const alreadyInitialized = widgetsWithClickTrackingInitialized.has(context);
  if (alreadyInitialized) return;
  widgetsWithClickTrackingInitialized.add(context);
  const inFroomleABGroup = isFroomleAbGroup();
  if (!inFroomleABGroup && !froomleSettings.sendNonFroomleClicksToFroomle)
    return;
  logDebug("Froomle clicks tracking enabled for widget", [context]);
  context.addEventListener("click", (event) => {
    var _a;
    const item = (_a = event.target) == null ? void 0 : _a.closest(
      froomleSettings.msntItemSelector
    );
    if (!item) return;
    sendFroomleEvent(
      "click_on_recommendation",
      pageType,
      extractParamsFromRecommendationItem(item),
      true,
      (eventParams) => {
        if (item.classList.contains(froomleSettings.froomleItemCls)) return;
        const widgetParams = widgetToWidgetParamsMap.get(context);
        if (!widgetParams) return;
        const { listConfig } = widgetParams;
        const request_id = inFroomleABGroup && item.classList.contains(froomleSettings.msntItemAbTestTargetCls) ? "not-loaded" : "motorsport";
        Object.assign(eventParams, {
          action_item: getFroomleItemId(getPlainIdForItem(item) ?? ""),
          action_item_type: "article",
          list_name: listConfig.name,
          request_id,
          user_group: "motorsport"
        });
      }
    );
  });
};
const extractParamsFromRecommendationItem = (item) => {
  const data = extractFroomleParamsFromItem(item);
  return data;
};
const showRecommendationsForWidgets = (() => {
  let listConfigsMapToProcess = /* @__PURE__ */ new Map();
  let itemsBeingReplaced = /* @__PURE__ */ new Set();
  const showRecommendationsForListsWithCleanup = (pageType, isInitial) => {
    showRecommendationsForLists(
      pageType,
      listConfigsMapToProcess,
      itemsBeingReplaced,
      isInitial
    );
    listConfigsMapToProcess = /* @__PURE__ */ new Map();
    itemsBeingReplaced = /* @__PURE__ */ new Set();
  };
  throttle(
    // small delay here is required to make sure that all initial widgets
    // are processed in a batch
    1,
    showRecommendationsForListsWithCleanup,
    {
      noLeading: true
    }
  );
  throttle(
    froomleSettings.batchPeriodWidgets,
    showRecommendationsForListsWithCleanup,
    {
      noLeading: true
    }
  );
  let initial = froomleSettings.loadFirstWidgetASAP;
  return (widgets) => {
    var _a;
    const pageType = getFroomlePageType();
    if (!pageType) return;
    if (!widgets.length || !froomleSettings.showRecommendations) {
      initial = false;
      return;
    }
    let initialLocal = initial;
    initial = false;
    let firstWidgetInitiallyInViewport = false;
    if (initialLocal) {
      const firstWidget = widgets[0];
      const firstWidgetRect = firstWidget.getBoundingClientRect();
      firstWidgetInitiallyInViewport = firstWidgetRect.top + (((_a = document.scrollingElement) == null ? void 0 : _a.scrollTop) ?? 0) <= window.innerHeight;
      if (firstWidgetInitiallyInViewport) {
        processWidgetsInViewport(firstWidget, true);
      }
    }
    const otherWidgets = firstWidgetInitiallyInViewport ? widgets.slice(1) : widgets;
    function processWidgetsInViewport(widget, isInitial = false) {
      var _a2, _b;
      const widgetParams = widgetToWidgetParamsMap.get(widget);
      if (!widgetParams || !pageType) return;
      const { listConfig, widgetName, widgetScope } = widgetParams;
      const widgetItemsSelector = resolveWidgetItemSelector(
        widget,
        listConfig.name
      );
      if (!isInitial) {
        observer.unobserve(widget);
      }
      if (!widgetItemsSelector) {
        return logDebug(
          `No selector returned for items inside widget`,
          [widget],
          "warn"
        );
      }
      const items = widget.querySelectorAll(widgetItemsSelector);
      if (!items.length) {
        return logDebug(
          `No items matched selector "${widgetItemsSelector}" in widget`,
          [widget],
          "warn"
        );
      }
      listConfigsMapToProcess.set(listConfig.name, {
        widget,
        listConfig,
        config: {
          list_size: listConfig.forceSize ?? items.length,
          ...(_b = (_a2 = froomleSettings.hooks).getAdditionalListFilters) == null ? void 0 : _b.call(
            _a2,
            froomleSettings,
            listConfig,
            widget,
            pageType
          )
        }
      });
      if (!listConfig.allContent) {
        items.forEach((item) => itemsBeingReplaced.add(item));
      }
      logDebug(
        `${isInitial ? "Initial load with timeout" : "Non initial load"}. Widget entered initialization area. Loading recommendations...`,
        [widget]
      );
      if (isInitial) {
        showRecommendationsForListsWithCleanup(pageType, isInitial);
      } else {
        showRecommendationsForListsWithCleanup(pageType, isInitial);
      }
    }
    if (!otherWidgets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const widget = entry.target;
          processWidgetsInViewport(widget);
        }
      },
      {
        rootMargin: froomleSettings.recommendationsMargin,
        threshold: froomleSettings.recommendationsThreshold
      }
    );
    otherWidgets.forEach((widget) => observer.observe(widget));
  };
})();
const showRecommendationsForLists = async (pageType, listConfigsMap, itemsBeingReplaced, isInitial) => {
  if (!listConfigsMap.size) return;
  const configMapEntriesArray = [...listConfigsMap.entries()];
  const recommendationsConfigs = configMapEntriesArray.map(
    ([listName, { config }]) => ({
      list_name: listName,
      ...config
    })
  );
  for (const [name, ongoingList] of ongoingRecommendationLists) {
    recommendationsConfigs.unshift(ongoingList);
  }
  recommendationsConfigs.forEach((config) => {
    ongoingRecommendationLists.set(config.list_name, config);
  });
  const recommendationsOrErrorReason = await getRecommendationsForLists(
    pageType,
    recommendationsConfigs,
    itemsBeingReplaced,
    isInitial
  );
  if (typeof recommendationsOrErrorReason === "string" || !(recommendationsOrErrorReason == null ? void 0 : recommendationsOrErrorReason.lists)) {
    logDebug(
      'No "lists" in recommendations request',
      [recommendationsOrErrorReason],
      "warn"
    );
    configMapEntriesArray.forEach(([name, cfg]) => {
      disableItemSkeletonInContainer(
        cfg.widget,
        true,
        true,
        typeof recommendationsOrErrorReason === "string" ? recommendationsOrErrorReason : void 0
      );
    });
    return;
  }
  const lastList = recommendationsOrErrorReason == null ? void 0 : recommendationsOrErrorReason.lists.at(-1);
  if (lastList) {
    const listConfig = listConfigsMap.get(lastList.list_name);
    if (!listConfig) return;
    const trackingProps = {
      list_name: lastList.list_name,
      request_id: recommendationsOrErrorReason.request_id,
      user_group: recommendationsOrErrorReason.user_group
    };
    listNameToRecommendationsMap.set(lastList.list_name, {
      list: lastList,
      trackingProps
    });
    updateWidgetItemsBasedOnFroomleRecommendations(
      pageType,
      listConfig.widget,
      lastList,
      // those will be required later for click and impression tracking
      trackingProps
    );
  }
  updateDuplicatedWidgetWithRecommendations();
};
const getRecommendationsForLists = async (pageType, lists, itemsBeingReplaced, isInitial) => {
  var _a;
  const allListsUniqueName = lists.reduce((acc, list) => {
    return acc + list.list_name + "__";
  }, "");
  try {
    const bodyParams = {
      ...getAdditionalRecommendationParams(pageType, itemsBeingReplaced),
      lists,
      channel: getChannel(),
      page_type: pageType,
      device_id: getDeviceId()
    };
    ongoingRecommendationListsExcludes.set(
      allListsUniqueName,
      ((_a = bodyParams.histories) == null ? void 0 : _a.exclude) ?? []
    );
    const userId = getUserId();
    if (userId) {
      bodyParams.user_id = userId;
    }
    const abtestVersion = getAbTestVersion();
    if (abtestVersion) {
      bodyParams.version = abtestVersion;
    }
    let abortSignals = null;
    if (isInitial) {
      abortSignals = [getBeforeUnloadAbortController().signal];
      abortSignals.push(
        AbortSignal.timeout(froomleSettings.initialRequestTimeout)
      );
    }
    const response = await fetch(
      `${froomleSettings.froomleUrl}/recommendations/requests`,
      {
        ...await getCommonAjaxParams(),
        body: JSON.stringify(bodyParams),
        signal: abortSignals ? AbortSignal.any(abortSignals) : null
      }
    );
    if (!response.ok) {
      if (response.status === 400) {
        const data = await response.json();
        logDebug(
          `Unable to fetch recommendations due to Bad request.`,
          [data.detail],
          "error"
        );
        return;
      }
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (err) {
    if (err === "beforeunload") {
      logDebug(
        `Page started unloading before recommendations loaded. Cancel recommendations request`,
        [lists]
      );
      return "page-unload";
    }
    if (err instanceof Error) {
      if (err.name === "TimeoutError") {
        logDebug(
          `Recommendations request aborted by timeout`,
          [lists, err],
          "warn"
        );
        return "aborted";
      } else if (err.name === "AbortError") {
        logDebug(
          `Recommendations request aborted by user`,
          [lists, err],
          "warn"
        );
        return "aborted-by-user";
      } else if (err.name === "TypeError" && err.message.startsWith("AbortSignal.")) {
        logDebug(`Abort signal is not supported`, [lists, err], "warn");
        return "abort-signal-not-supported";
      } else {
        logDebug(
          `Failed to retrieve recommendations for lists`,
          [lists, err],
          "error"
        );
        return `${err.name}: ${err.message}`;
      }
    }
    logDebug(
      `Failed to retrieve recommendations for lists`,
      [lists, err],
      "error"
    );
    return "recommendations-error";
  } finally {
    lists.forEach((list) => {
      ongoingRecommendationLists.delete(list.list_name);
    });
    ongoingRecommendationListsExcludes.delete(allListsUniqueName);
  }
};
const getBeforeUnloadAbortController = (() => {
  const controller = new AbortController();
  window.addEventListener(
    "beforeunload",
    () => controller.abort("beforeunload")
  );
  return () => controller;
})();
const getAdditionalRecommendationParams = (pageType, itemsBeingReplaced) => {
  var _a;
  const params = {};
  const pageData = (_a = window.dataLayer) == null ? void 0 : _a[0];
  if (!pageData) return params;
  const exclude = getExcludedItems(pageType, itemsBeingReplaced);
  for (const [
    uniqueName,
    ongoingExclude
  ] of ongoingRecommendationListsExcludes) {
    ongoingExclude.forEach((item) => {
      exclude.push(item);
    });
  }
  if (exclude.length) {
    if (!params.histories) {
      params.histories = {};
    }
    params.histories.exclude = exclude;
  }
  if (pageType === "article_detail") {
    Object.assign(params, {
      context_item: getFroomleItemId(pageData.entityId),
      context_item_type: pageData.entityType
    });
  }
  return params;
};
const getExcludedItems = (pageType, itemsBeingReplaced) => {
  if (!itemsBeingReplaced.size) return [];
  const isExcludedItem = getIsExcludedItem(
    getAllItemsInReplacedWidgets(itemsBeingReplaced)
  );
  const ids = Array.from(
    document.body.querySelectorAll(
      froomleSettings.msntItemSelector
    )
  ).filter((item) => isExcludedItem(item)).map((item) => {
    const id = item.dataset.entityId;
    if (!id) return;
    return getFroomleItemId(id);
  }).filter((item) => !!item);
  return ids.map((id) => ({
    id,
    item_type: "article",
    reconsumable: false
  }));
};
const getAllItemsInReplacedWidgets = (itemsBeingReplaced) => {
  const allWidgets = /* @__PURE__ */ new Set();
  itemsBeingReplaced.forEach((item) => {
    const widget = getItemWidget(item);
    if (!widget) return;
    allWidgets.add(widget);
  });
  const allItemsInReplacedWidgets = /* @__PURE__ */ new Set();
  allWidgets.forEach((widget) => {
    widget.querySelectorAll(froomleSettings.msntItemSelector).forEach((item) => {
      allItemsInReplacedWidgets.add(item);
    });
  });
  return allItemsInReplacedWidgets;
};
const getIsExcludedItem = (allItemsInReplacedWidgets) => {
  let reachedCurrentWidget = false;
  let lastWidgetName;
  return (item) => {
    var _a;
    const widget = getItemWidget(item);
    if (!widget) return false;
    const currentWidgetName = getWidgetName(widget);
    const isCheckAll = widget.dataset.excludeContentBehavior === "check-all";
    if (reachedCurrentWidget && froomleSettings.excludeContentOnlyUntilWidget) {
      if (currentWidgetName !== lastWidgetName) return false;
      if (!isCheckAll) return false;
    }
    const itemPresentInWidget = allItemsInReplacedWidgets.has(item);
    const shouldExclude = itemPresentInWidget ? !item.classList.contains(froomleSettings.msntSkeletonItemCls) : true;
    if (itemPresentInWidget && reachedCurrentWidget === false) {
      reachedCurrentWidget = true;
    }
    if (reachedCurrentWidget) {
      const widget2 = getItemWidget(item);
      if (widget2) {
        if (!lastWidgetName) {
          lastWidgetName = getWidgetName(widget2);
        }
      }
    } else {
      if (currentWidgetName) {
        const widgetListName = ((_a = widgetToWidgetParamsMap.get(widget)) == null ? void 0 : _a.listConfig.name) ?? "";
        const itemPresentInPreviousWidget = ongoingRecommendationLists.has(widgetListName);
        if (itemPresentInPreviousWidget) return false;
      }
    }
    return item.dataset.entityType === "article" && shouldExclude && item.offsetParent;
  };
};
let initialized$2 = false;
const startWidgetsTracking = () => {
  if (initialized$2) return;
  initialized$2 = true;
  const pageType = getFroomlePageType();
  if (!pageType) return;
  const defaultContext = document.body;
  startWidgetsTrackingInContext(defaultContext);
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== "childList") continue;
      mutation.addedNodes.forEach((el) => {
        if (el.nodeType !== 1) return;
        startWidgetsTrackingInContext(el);
      });
    }
  });
  observer.observe(defaultContext, {
    subtree: true,
    childList: true
  });
};
const initWidgetsAwaitingConsent = () => {
  if (!widgetsAwaitingConsent.size) return;
  for (const widget of widgetsAwaitingConsent) {
    startWidgetsTrackingInContext(widget);
  }
  widgetsAwaitingConsent.clear();
};
const startWidgetsTrackingInContext = (context) => {
  const pageType = getFroomlePageType();
  if (context.children.length === 0 || !pageType) return;
  const widgets = Object.entries(froomleSettings.lists).reduce(
    (acc, [widgetNameWithScope, listConfig]) => {
      const targetSelector = getWidgetSelectorFromListName(widgetNameWithScope);
      if (!targetSelector) return acc;
      const [widgetName, widgetScope] = widgetNameWithScope.split("::");
      let widgets2;
      if (context.matches(targetSelector)) {
        widgets2 = [context];
      } else {
        widgets2 = Array.from(
          context.querySelectorAll(targetSelector)
        );
      }
      widgets2.forEach((widget) => {
        if (initializedWidgetsSet.has(widgetNameWithScope)) {
          if (!initializedWidgetElementsSet.has(widget)) {
            logDebug("Widget duplicate detected", [widget]);
            if (!widgetToWidgetParamsMap.has(widget)) {
              widgetToWidgetParamsMap.set(widget, {
                duplicate: true,
                widgetName,
                widgetScope,
                listConfig
              });
            }
            startRecommendationsClickTrackingInWidget(widget);
            startFroomleAnalyticsForWidget(widget);
            if (!froomleSettings.showRecommendations) {
              logDebug(
                "Recommendations disabled, updating items based on noRecommendationAction",
                [widget],
                "warn"
              );
              disableItemSkeletonInContainer(widget, true);
              trackImpressionsForItemsInBlock(pageType, widget);
            } else {
              initializedWidgetElementsSet.add(widget);
              duplicatedWidgetsToProcess.add(widget);
            }
          }
          return;
        }
        if (!widgetToWidgetParamsMap.has(widget)) {
          widgetToWidgetParamsMap.set(widget, {
            duplicate: false,
            widgetName,
            widgetScope,
            listConfig
          });
        }
        startRecommendationsClickTrackingInWidget(widget);
        startFroomleAnalyticsForWidget(widget);
        if (!froomleSettings.showRecommendations) {
          logDebug(
            "Recommendations disabled, updating items based on noRecommendationAction",
            [widget],
            "warn"
          );
          disableItemSkeletonInContainer(widget, true);
          trackImpressionsForItemsInBlock(pageType, widget);
          return;
        }
        const widgetItemsSelector = resolveWidgetItemSelector(
          widget,
          listConfig.name
        );
        if (!widgetItemsSelector) return;
        const item = widget.querySelector(widgetItemsSelector);
        if (!(item == null ? void 0 : item.offsetParent)) {
          if (isFroomleAbGroup()) return;
        }
        initializedWidgetsSet.add(widgetNameWithScope);
        initializedWidgetElementsSet.add(widget);
        acc.push(widget);
      });
      return acc;
    },
    []
  );
  if (widgets.length) {
    logDebug("Widgets tracking enabled for", widgets);
  }
  showRecommendationsForWidgets(widgets);
  updateDuplicatedWidgetWithRecommendations();
};
const resolveWidgetItemSelector = (widget, listName) => {
  var _a, _b, _c, _d, _e;
  const [widgetName, widgetScope] = listName.split("::");
  let widgetItemsSelector = (_b = (_a = froomleSettings.hooks) == null ? void 0 : _a.getWidgetItemsSelector) == null ? void 0 : _b.call(
    _a,
    froomleSettings,
    widget,
    widgetName,
    widgetScope
  );
  if (!widgetItemsSelector) {
    if (widgetScope) {
      widgetItemsSelector = (_d = (_c = froomleSettings.scopedBlocksItemSelectors) == null ? void 0 : _c[widgetScope]) == null ? void 0 : _d[widgetName];
    } else {
      widgetItemsSelector = (_e = froomleSettings.nonScopedBlocksItemSelectors) == null ? void 0 : _e[widgetName];
      if (!widgetItemsSelector) {
        widgetItemsSelector = froomleSettings.msntSkeletonItemSelector;
      }
    }
  }
  return widgetItemsSelector;
};
const getWidgetSelectorFromListName = (widgetNameWithScope) => {
  var _a, _b, _c, _d, _e;
  const [widgetName, widgetScope] = widgetNameWithScope.split("::");
  let result = void 0;
  result = (_b = (_a = froomleSettings.hooks) == null ? void 0 : _a.getWidgetSelector) == null ? void 0 : _b.call(
    _a,
    froomleSettings,
    widgetName,
    widgetScope
  );
  if (!result) {
    if (widgetScope) {
      result = (_d = (_c = froomleSettings.scopedBlocksSelectors) == null ? void 0 : _c[widgetScope]) == null ? void 0 : _d[widgetName];
    } else {
      result = (_e = froomleSettings.nonScopedBlocksSelectors) == null ? void 0 : _e[widgetName];
      if (!result) {
        result = `[data-widget="${widgetName}"]`;
      }
    }
  }
  if (!result) {
    logDebug(
      `No selector returned for widget "${widgetName}"`,
      void 0,
      "warn"
    );
  }
  return result;
};
const getWidgetItemSelectorFromListName = (listName) => {
  const widgetSelector = getWidgetSelectorFromListName(listName);
  if (!widgetSelector) return;
  const widget = document.querySelector(widgetSelector);
  if (!widget) return;
  return resolveWidgetItemSelector(widget, listName);
};
const getWidgetName = (widget) => {
  if (!widget) return;
  const widgetName = widget.dataset.widget;
  if (!widgetName) {
    logDebug('Missing "data-widget" attribute for widget', [widget], "error");
  }
  return widgetName;
};
const updateWidgetItemsBasedOnFroomleRecommendations = (pageType, widget, recommendationsList, trackingProps) => {
  const widgetParams = widgetToWidgetParamsMap.get(widget);
  if (!widgetParams) return;
  const { listConfig } = widgetParams;
  let widgetItemsSelector = resolveWidgetItemSelector(widget, listConfig.name);
  const items = widget.querySelectorAll(widgetItemsSelector);
  items.forEach((item, index) => {
    const recommendation = recommendationsList.items[index];
    if (!recommendation) {
      logDebug(
        `No recommendation available in widget at index ${index}. Widget/item/recommendations are:`,
        [widget, item, recommendationsList],
        "warn"
      );
      item.classList.add(froomleSettings.froomleMissingRecommendationCls);
      disableSkeletonForItem(item, true);
      return;
    }
    updateItemToFroomleRecommendation(
      item,
      recommendation,
      trackingProps,
      widget
    );
  });
  logDebug(
    `Widget updated to show recommendations. Widget/recommendations are:`,
    [widget, recommendationsList]
  );
  trackImpressionsForItemsInBlock(pageType, widget);
};
const updateDuplicatedWidgetWithRecommendations = () => {
  const pageType = getFroomlePageType();
  if (!pageType || !duplicatedWidgetsToProcess.size || !listNameToRecommendationsMap.size)
    return;
  for (const widget of duplicatedWidgetsToProcess) {
    const widgetName = getWidgetName(widget);
    if (!widgetName) continue;
    const froomleListName = froomleSettings.lists[widgetName].name;
    const widgetRecommendationsCfg = listNameToRecommendationsMap.get(froomleListName);
    if (!widgetRecommendationsCfg) continue;
    duplicatedWidgetsToProcess.delete(widget);
    const { list, trackingProps } = widgetRecommendationsCfg;
    logDebug(
      `Previous configuration for duplicated widget available. Updating based on previous config`,
      [widget, list]
    );
    updateWidgetItemsBasedOnFroomleRecommendations(
      pageType,
      widget,
      list,
      // those will be required later for click and impression tracking
      trackingProps
    );
  }
};
const handleNoRecommendationsForDuplicatedWidget = (duplicatedWidgetName, noRecommendation = false, onlyVisible, froomleCustomStatus) => {
  for (const widget of duplicatedWidgetsToProcess) {
    const widgetName = getWidgetName(widget);
    if (!widgetName || duplicatedWidgetName !== widgetName) continue;
    duplicatedWidgetsToProcess.delete(widget);
    disableItemSkeletonInContainer(
      widget,
      noRecommendation,
      onlyVisible,
      froomleCustomStatus
    );
  }
};
const extractFroomleParamsFromItem = (item) => {
  let data = itemsTrackingParamsMap.get(item);
  if (!data) {
    data = extractFroomleParamsFromItemNonFroomle(item);
  }
  return data ?? {};
};
const extractFroomleParamsFromItemNonFroomle = (item) => {
  const data = {
    action_item_type: "article",
    user_group: "motorsport",
    request_id: "motorsport",
    action_item: getFroomleItemId(getPlainIdForItem(item) ?? "")
  };
  const widget = getItemWidget(item);
  if (!widget) return data;
  const widgetRecommendationsCfg = widgetToWidgetParamsMap.get(widget);
  if (widgetRecommendationsCfg) {
    data.list_name = widgetRecommendationsCfg.listConfig.name;
  }
  return data;
};
const updateItemToFroomleRecommendation = (item, recommendation, trackingProps, widget) => {
  var _a, _b;
  const widgetParams = widgetToWidgetParamsMap.get(widget);
  if (!widgetParams) return;
  const { widgetName, widgetScope } = widgetParams;
  itemsTrackingParamsMap.set(item, {
    ...trackingProps,
    action_item: recommendation.item_id,
    action_item_type: "article"
  });
  if (((_b = (_a = froomleSettings == null ? void 0 : froomleSettings.hooks) == null ? void 0 : _a.updateWidgetItem) == null ? void 0 : _b.call(
    _a,
    froomleSettings,
    item,
    recommendation,
    widget,
    widgetName,
    widgetScope
  )) === true)
    return;
  if (widgetScope) {
    if (froomleSettings.updateItemToFroomleRecommendationScoped(
      froomleSettings,
      item,
      recommendation,
      widget
    ))
      return;
    logDebug(
      `No update method provided for scoped item. WidgetName/item/widget`,
      [`${widgetName}::${widgetScope}`, item, widget],
      "warn"
    );
    return;
  }
  froomleSettings.updateItemToFroomleRecommendation(
    froomleSettings,
    item,
    recommendation,
    widget
  );
};
const updateItemImages = (item, recommendation) => {
  const picture = item.querySelector("picture");
  if (!picture) return;
  const sources = picture.querySelectorAll("source");
  if (!sources.length) return;
  const img = picture.querySelector("img");
  if (!img) return;
  const baseImg = recommendation.images[0];
  const newImg = img.cloneNode();
  newImg.src = baseImg;
  newImg.setAttribute("alt", recommendation.title);
  img.replaceWith(newImg);
  const [imgPathBeforeSize, imgPathAfterSizeNoExtension] = baseImg.replace(/\.\w+$/, "").split(/\/s\d+\//);
  sources.forEach((source) => {
    const targetExtension = froomleSettings.mediaTypeToExtension[source.type];
    if (!targetExtension) {
      logDebug(
        `No media type mapping provided for "${source.type}"`,
        void 0,
        "warn"
      );
      return;
    }
    const sizesWithoutExtension = [];
    let currentSize = froomleSettings.minImageSize;
    while (currentSize <= froomleSettings.maxImageSize) {
      sizesWithoutExtension.push(
        `${imgPathBeforeSize}/s${currentSize}/${imgPathAfterSizeNoExtension}.${targetExtension} ${currentSize}w`
      );
      currentSize += froomleSettings.imageSizesStep;
    }
    source.srcset = sizesWithoutExtension.join(",");
  });
};
const getFroomleItemId = (itemId) => `${froomleSettings.idPrefix}-${itemId}`;
const getPlainIdForItem = (item) => {
  const id = item.dataset.entityId ?? item.dataset.value;
  if (!id) return;
  return id.includes("-") ? id.split("-").at(-1) : id;
};
const formatItemId = (itemId) => itemId.split("-").at(-1) ?? "";
const disableItemSkeletonInContainer = (container = document.body, noRecommendation = false, onlyVisible = true, froomleCustomStatus) => {
  const skeletonItemCls = froomleSettings.msntSkeletonItemCls;
  const items = container.querySelectorAll(`.${skeletonItemCls}`);
  disableSkeletonForItems(
    items,
    noRecommendation,
    onlyVisible,
    froomleCustomStatus
  );
  const widgetName = getWidgetName(container);
  if (widgetName) {
    handleNoRecommendationsForDuplicatedWidget(
      widgetName,
      noRecommendation,
      onlyVisible,
      froomleCustomStatus
    );
  }
};
const disableSkeletonForItems = (items, noRecommendation = false, onlyVisible = false, froomleCustomStatus) => {
  items.forEach((item) => {
    disableSkeletonForItem(
      item,
      noRecommendation,
      onlyVisible,
      froomleCustomStatus
    );
  });
};
const disableSkeletonForItem = (item, noRecommendation = false, onlyVisible = false, froomleCustomStatus) => {
  var _a;
  if (onlyVisible && !item.offsetParent) return;
  const skeletonItemCls = froomleSettings.msntSkeletonItemCls;
  const abtestItemCls = froomleSettings.msntItemAbTestTargetCls;
  const widget = getItemWidget(item);
  if (noRecommendation && ((_a = widget == null ? void 0 : widget.dataset) == null ? void 0 : _a.noRecommendationAction) === "delete") {
    logDebug(
      'No recommendation for item and noRecommendationAction is "delete"',
      [item, widget],
      "warn"
    );
    item.remove();
    notifyWidgetIfNoItems(widget);
  } else {
    item.classList.remove(skeletonItemCls);
    item.classList.add(abtestItemCls);
    if (froomleCustomStatus) {
      item.dataset.froomleCustomStatus = froomleCustomStatus;
    }
  }
};
const getItemWidget = (item) => item.closest("[data-widget]");
const notifyWidgetIfNoItems = (widget) => {
  const items = widget.querySelectorAll(
    froomleSettings.msntItemAbTestTargetSelector
  );
  if (items.length) return;
  widget.classList.add(froomleSettings.widgetNoRecommendationsCls);
  widget.dispatchEvent(new CustomEvent("msnt-froomle-no-items"));
  logDebug(`No recommendation for widget, it is empty now`, [widget], "warn");
};
let initialized$1 = false;
const startPageViewsTracking = () => {
  if (initialized$1 || !froomleSettings.sendPageViews) return;
  initialized$1 = true;
  const pageType = getFroomlePageType();
  logDebug(`Page views tracking - ${pageType ? "enabled" : "disabled"}`);
  if (!pageType) return;
  switch (pageType) {
    case "article_detail":
      return trackDetailPageView(pageType);
    case "home":
      return trackPageVisit(pageType);
  }
};
const trackDetailPageView = (pageType) => {
  var _a, _b;
  const actionItem = ((_a = window.dataLayer) == null ? void 0 : _a[0].entityId) ?? ((_b = window.dataLayer) == null ? void 0 : _b[0].articleId);
  sendFroomleEvent("detail_pageview", pageType, {
    action_item: getFroomleItemId(actionItem),
    action_item_type: "article"
  });
};
const trackPageVisit = (pageType) => {
  sendFroomleEvent("page_visit", pageType, null);
};
const setDefaults = (settings) => {
  mergeAndNormalizeSettings(settings);
};
let apiRef = void 0;
let consentGranted = false;
let initialized = false;
const init = async (settings) => {
  if (initialized) return apiRef;
  initialized = true;
  mergeAndNormalizeSettings(settings);
  if (!isSettingsValid(froomleSettings)) {
    logDebug(
      "Provided settings are invalid, updating items based on noRecommendationAction",
      [settings],
      "warn"
    );
    disableItemSkeletonInContainer(
      document.body,
      true,
      true,
      "invalid-settings"
    );
    return Promise.resolve();
  }
  if (!(froomleSettings == null ? void 0 : froomleSettings.useProxyForAllRequests)) {
    const token = await getOAuthToken();
    if (!token) {
      logDebug(
        "Unable to retrieve token, updating items based on noRecommendationAction",
        [settings],
        "warn"
      );
      disableItemSkeletonInContainer(
        document.body,
        true,
        true,
        "no-auth-token"
      );
      return;
    }
  }
  apiRef = {
    config: froomleSettings,
    updateItemImages,
    startWidgetsTracking: (widget) => {
      logDebug('API call "startWidgetsTracking" for widget => ', [widget]);
      if (consentGranted) return startWidgetsTrackingInContext(widget);
      logDebug(`No consent from user yet. Widget added to waiting list.`, [
        widget
      ]);
      widgetsAwaitingConsent.add(widget);
    },
    isFroomleAbGroup,
    // we have to keep tracking consent value to allow initialization of
    // dynamic widgets if user grants consent eventually
    setConsentState: (newState) => {
      consentGranted = newState;
      if (!started) {
        if (consentGranted) {
          logDebug("Consent granted. Starting...");
          start();
        } else {
          logDebug(`Consent rejected. Froomle won't start`, void 0, "warn");
          disableItemSkeletonInContainer(
            document.body,
            true,
            true,
            "no-consent"
          );
        }
      }
      if (!consentGranted) return;
      initWidgetsAwaitingConsent();
    },
    getWidgetSelectorFromListName,
    getWidgetItemSelectorFromListName
  };
  window.msntGlobals = window.msntGlobals || {};
  window.msntGlobals.froomleApi = apiRef;
  window.dispatchEvent(new CustomEvent("msnt-froomle-api-ready"));
  return apiRef;
};
let started = false;
const start = () => {
  if (started) return;
  started = true;
  startWidgetsTracking();
  startPageViewsTracking();
};
export {
  deviceIdKey,
  disableItemSkeletonInContainer,
  disableSkeletonForItem,
  disableSkeletonForItems,
  duplicatedWidgetsToProcess,
  extractFroomleParamsFromItem,
  formatItemId,
  froomleOAuthTokenKey,
  froomleSettings,
  getFroomleItemId,
  getItemWidget,
  getPlainIdForItem,
  init,
  initializedWidgetElementsSet,
  initializedWidgetsSet,
  itemsTrackingParamsMap,
  listNameToRecommendationsMap,
  ongoingRecommendationLists,
  ongoingRecommendationListsExcludes,
  setDefaults,
  updateItemImages,
  updateItemToFroomleRecommendation,
  widgetToWidgetParamsMap,
  widgetsAwaitingConsent,
  widgetsWithClickTrackingInitialized
};
//# sourceMappingURL=froomle.js.map
